// The accident and incident register (Session AOM). Office/admin. A filterable list with a
// "Record an accident" modal; a row opens the detail page.
//
// THE REGISTER VIEW DELIBERATELY CARRIES NO PERSONAL DETAIL. The API's list DTO does not return the
// injured person's name, contact details or injury detail (only `injuredPersonRecorded`, a boolean),
// so this screen cannot show them even by accident. It is the screen most likely to be left open on
// a desk with somebody else in the room.
//
// THE RIDDOR COLUMN HAS THREE STATES. "Not assessed" is its own answer, shown in amber, because it
// is the one that needs somebody to act: an assessor asking "have you had any RIDDOR reportable
// accidents in the last 3 years" needs the difference between "no" and "nobody has looked".
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import {
  api,
  ApiError,
  type AccidentEventType,
  type AccidentHospitalAdmission,
  type AccidentListFilters,
  type AccidentListItem,
  type AccidentStatus,
} from "../../lib/api";
// The shared resource clients, matching EnvironmentalRecordsPage's own site/lift picker: this
// register tags a site and a lift the same way that one does.
import { assets as assetsResource, type Asset } from "../../lib/resources";
import { RecordPicker } from "../../components/RecordPicker";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDateTime } from "../../lib/format";
import { SortableHeader } from "../../components/SortableHeader";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import {
  EVENT_TYPES,
  EVENT_TYPE_LABEL,
  HOSPITAL_LABEL,
  HOSPITAL_OPTIONS,
  STATUS_LABEL,
  riddorKey,
  writtenReportCountdown,
} from "./accidentsUi";

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "under_investigation", label: "Under investigation" },
  { value: "reported_to_hse", label: "Reported to the HSE" },
  { value: "closed", label: "Closed" },
];

// M-10, Session AQU: what the register's search box matches. ONLY fields the row already shows
// (reference, type, site or location, ELE number). The list DTO carries no personal detail, and
// the description it does carry is not shown on this screen, so matching on it would surface rows
// for a reason the office cannot see. Nothing is added to a row by searching.
const ACCIDENT_SEARCH_ACCESSORS: SearchAccessors<AccidentListItem> = [
  (r) => r.ref,
  (r) => EVENT_TYPE_LABEL[r.eventType] ?? r.eventType,
  (r) => r.siteName,
  (r) => r.location,
  (r) => r.eleNumber,
];

// The status order a sort follows: the register's own workflow order, as the filter tabs list it.
const STATUS_ORDER: Record<string, number> = { open: 0, under_investigation: 1, reported_to_hse: 2, closed: 3 };

// Sort values are the underlying facts, never the formatted cell text. RIDDOR sorts "Not assessed"
// first on ascending, because it is the answer that needs somebody to act.
const ACCIDENT_SORT_ACCESSORS: SortAccessors<AccidentListItem> = {
  ref: (r) => r.ref,
  when: (r) => {
    const t = Date.parse(r.dateOfEvent);
    return Number.isNaN(t) ? null : t;
  },
  type: (r) => EVENT_TYPE_LABEL[r.eventType] ?? r.eventType,
  where: (r) => r.siteName || r.location || null,
  riddor: (r) => (r.riddorReportable === null ? 0 : r.riddorReportable ? 1 : 2),
  status: (r) => STATUS_ORDER[r.status] ?? null,
  deadline: (r) => r.daysToWrittenReport,
};

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function AccidentsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AccidentListItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [eventType, setEventType] = useState("");
  const [reportable, setReportable] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  // Below md the register renders as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    const filters: AccidentListFilters = {};
    if (status) filters.status = status as AccidentStatus;
    if (eventType) filters.eventType = eventType as AccidentEventType;
    if (reportable) filters.reportable = reportable as "yes" | "no" | "unassessed";
    if (showArchived) filters.archived = "include";
    api.accidents
      .list(filters)
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status, eventType, reportable, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  // M-10, Session AQU: a client-side search and sort over the rows the (unchanged) server filters
  // returned. The search term lives only in the URL's ?q=, which the shared hook writes.
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("accidents");
  const shown = rows ? sortRows(filterRows(rows, search, ACCIDENT_SEARCH_ACCESSORS), ACCIDENT_SORT_ACCESSORS, sort) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        title="Accidents and incidents"
        className="mb-4"
        subtitle="Workplace accidents, injuries and near misses, their RIDDOR assessment and the investigation."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            Record an accident
          </Button>
        }
      />

      {/* The filters are always shown, loaded or not: they drive the server query, so hiding them
          when a filter returns nothing would strand the office on an empty register. */}
      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reference, type, site or location..."
        searchLabel="Search accidents and incidents"
        filters={
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value || "all"}
                  type="button"
                  aria-pressed={status === f.value}
                  onClick={() => setStatus(f.value)}
                  className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                    status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <label className="w-full text-xs sm:w-auto">
              <span className={labelCls}>RIDDOR</span>
              <select aria-label="RIDDOR" value={reportable} onChange={(e) => setReportable(e.target.value)} className={`${inputCls} w-full min-w-0 sm:w-auto`}>
                <option value="">All</option>
                <option value="yes">Reportable</option>
                <option value="no">Not reportable</option>
                <option value="unassessed">Not assessed</option>
              </select>
            </label>
            <label className="w-full text-xs sm:w-auto">
              <span className={labelCls}>Type of event</span>
              <select aria-label="Type of event" value={eventType} onChange={(e) => setEventType(e.target.value)} className={`${inputCls} w-full min-w-0 sm:w-auto`}>
                <option value="">All</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {EVENT_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-ele-body max-sm:min-h-11">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-ele-green" />
              Show archived
            </label>
          </div>
        }
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the accident register. Please try again.
        </p>
      ) : rows === null || shown === null ? (
        <Loader label="Loading the accident register..." />
      ) : rows.length === 0 ? (
        /* The empty state is the one the Director will see for a long time, and it should read as a
           clean record rather than as a broken page. It also names the CHAS answer this register
           exists to back up. */
        <EmptyState
          message="No accidents or incidents recorded."
          hint="That is the answer to give an assessor asking about RIDDOR reportable accidents. Open any record and use Download document to produce a blank copy of the accident report form."
        />
) : shown.length === 0 ? (
        <EmptyState role="status" message={`No accidents or incidents match "${search.trim()}".`} />
      ) : mobileCards ? (
        /* Below md the six-column table cannot share 375px. Each record is a card instead: the
           reference never truncates, the type, place and deadline the table hid are shown, and the
           whole card opens the record exactly as the row does. */
        <MobileCardList label="Accidents and incidents">
          {shown.map((r) => {
            const countdown = writtenReportCountdown(r.daysToWrittenReport);
            return (
              <MobileListCard
                key={r.id}
                to={`/office/accidents/${r.id}`}
                primary={
                  <>
                    {r.ref}
                    {r.archivedAt && <span className="ml-2 font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Archived</span>}
                  </>
                }
                secondary={
                  <>
                    {EVENT_TYPE_LABEL[r.eventType] ?? r.eventType}
                    {/* Somebody was hurt, without saying who: the list DTO carries no name. */}
                    {r.injuredPersonRecorded && <span className="block text-xs opacity-70">Injured person recorded</span>}
                  </>
                }
                meta={
                  <>
                    <span className="block">{formatDateTime(r.dateOfEvent)}</span>
                    <span className="block">
                      {r.siteName || r.location || "-"}
                      {r.eleNumber ? ` · ${r.eleNumber}` : ""}
                    </span>
                    {countdown && <span className={`block ${countdown.urgent ? "text-ele-danger" : ""}`}>{countdown.text}</span>}
                  </>
                }
                right={
                  <>
                    <StatusPill domain="riddor" status={riddorKey(r.riddorReportable)} />
                    <StatusPill word={STATUS_LABEL[r.status] ?? r.status} tone={statusEntry("accident", r.status).tone} />
                  </>
                }
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "14%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "13%" }} />
              <col className="max-md:hidden" style={{ width: "13%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="When" colKey="when" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Where" colKey="where" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="RIDDOR" colKey="riddor" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Deadline" colKey="deadline" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const countdown = writtenReportCountdown(r.daysToWrittenReport);
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/office/accidents/${r.id}`)}
                    className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface"
                  >
                    <td className="px-3 py-2 font-semibold text-ele-strong">
                      {r.ref}
                      {r.archivedAt && <span className="ml-2 font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Archived</span>}
                    </td>
                    <td className="px-3 py-2 text-ele-body">{formatDateTime(r.dateOfEvent)}</td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">
                      {EVENT_TYPE_LABEL[r.eventType] ?? r.eventType}
                      {/* Somebody was hurt, without saying who: the list DTO carries no name. */}
                      {r.injuredPersonRecorded && <span className="block text-xs opacity-70">Injured person recorded</span>}
                    </td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">
                      {r.siteName || r.location || "-"}
                      {r.eleNumber && <span className="block text-xs opacity-70">{r.eleNumber}</span>}
                    </td>
                    <td className="cell-actions px-3 py-2">
                      <StatusPill domain="riddor" status={riddorKey(r.riddorReportable)} />
                    </td>
                    <td className="cell-actions px-3 py-2">
                      <StatusPill word={STATUS_LABEL[r.status] ?? r.status} tone={statusEntry("accident", r.status).tone} />
                    </td>
                    <td className={`max-md:hidden px-3 py-2 text-xs ${countdown?.urgent ? "text-ele-danger" : "text-ele-body"}`}>
                      {countdown ? countdown.text : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddAccidentModal
          onClose={() => setAdding(false)}
          onAdded={(id) => {
            setAdding(false);
            navigate(`/office/accidents/${id}`);
          }}
        />
      )}
    </div>
  );
}

// The create form. It asks ONLY for what happened: the RIDDOR determination and the investigation
// are separate, later decisions made on the detail page, and putting a category picker here would
// invite somebody to make a legal determination while still writing down the facts.
function AddAccidentModal({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  const [form, setForm] = useState({
    dateOfEvent: localNow(),
    eventType: "employee_injury" as AccidentEventType,
    siteId: "",
    assetId: "",
    location: "",
    description: "",
    immediateAction: "",
    witnesses: "",
    injuredName: "",
    injuredRole: "",
    injuredEmployer: "",
    injuredContact: "",
    natureOfInjury: "",
    bodyPartAffected: "",
    hospitalAdmission: "" as AccidentHospitalAdmission | "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The lift list follows the chosen site. Changing the site CLEARS any lift already picked: a
  // stale assetId would file the accident against a lift in a different building, the same
  // stale-value trap RaiseWorkModal's own lift handler exists for.
  const assetsState = useOptionsLoader<Asset[]>(
    () => (form.siteId ? assetsResource.list({ siteId: form.siteId }) : Promise.resolve([])),
    [form.siteId],
  );

  const set =
    (name: keyof typeof form) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.description.trim()) {
      setErr("Describe what happened.");
      return;
    }
    if (!form.dateOfEvent) {
      setErr("Enter when it happened.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.accidents.create({
        dateOfEvent: form.dateOfEvent,
        eventType: form.eventType,
        siteId: form.siteId || null,
        assetId: form.assetId || null,
        location: form.location.trim() || null,
        description: form.description.trim(),
        immediateAction: form.immediateAction.trim() || null,
        witnesses: form.witnesses.trim() || null,
        injuredName: form.injuredName.trim() || null,
        injuredRole: form.injuredRole.trim() || null,
        injuredEmployer: form.injuredEmployer.trim() || null,
        injuredContact: form.injuredContact.trim() || null,
        natureOfInjury: form.natureOfInjury.trim() || null,
        bodyPartAffected: form.bodyPartAffected.trim() || null,
        hospitalAdmission: form.hospitalAdmission || null,
      });
      onAdded(created.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the record. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Record an accident or incident" onClose={() => !busy && onClose()} width="max-w-2xl" margin="my-8" panelClassName="p-6">
      <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-strong">Record an accident or incident</h2>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={labelCls}>When did it happen</span>
          <input aria-label="When did it happen" type="datetime-local" value={form.dateOfEvent} onChange={set("dateOfEvent")} className={`${inputCls} w-full`} />
        </label>
        <label>
          <span className={labelCls}>Type of event</span>
          <select aria-label="Type of event" value={form.eventType} onChange={set("eventType")} className={`${inputCls} w-full`}>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EVENT_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <div>
          <RecordPicker
            kind="site"
            label="Site"
            value={form.siteId}
            placeholder="Search sites (leave empty if not at a site on the register)"
            onChange={(id) => setForm((f) => ({ ...f, siteId: id, assetId: "" }))}
          />
        </div>
        <label>
          <span className={labelCls}>Lift</span>
          <select
            aria-label="Lift"
            value={form.assetId}
            onChange={set("assetId")}
            disabled={!form.siteId}
            className={`${inputCls} w-full disabled:opacity-50`}
          >
            <option value="">{form.siteId ? "No particular lift" : "Choose a site first"}</option>
            {(assetsState.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.eleNumber ?? a.legacyEleNumber ?? a.id}
              </option>
            ))}
          </select>
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Where exactly</span>
          <input aria-label="Where exactly" value={form.location} onChange={set("location")} placeholder="e.g. Ground floor lift lobby" className={`${inputCls} w-full`} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>What happened</span>
          <textarea aria-label="What happened" value={form.description} onChange={set("description")} rows={3} className={`${inputCls} w-full`} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Immediate action taken</span>
          <textarea aria-label="Immediate action taken" value={form.immediateAction} onChange={set("immediateAction")} rows={2} className={`${inputCls} w-full`} />
        </label>

        <p className="sm:col-span-2 mt-1 font-menu text-xs uppercase tracking-wide text-ele-blue">Injured person, if there was one</p>
        <label>
          <span className={labelCls}>Name</span>
          <input aria-label="Injured person name" value={form.injuredName} onChange={set("injuredName")} className={`${inputCls} w-full`} />
        </label>
        <label>
          <span className={labelCls}>Role or occupation</span>
          <input aria-label="Injured person role" value={form.injuredRole} onChange={set("injuredRole")} className={`${inputCls} w-full`} />
        </label>
        <label>
          <span className={labelCls}>Employer</span>
          <input aria-label="Injured person employer" value={form.injuredEmployer} onChange={set("injuredEmployer")} className={`${inputCls} w-full`} />
        </label>
        <label>
          <span className={labelCls}>Contact</span>
          <input aria-label="Injured person contact" value={form.injuredContact} onChange={set("injuredContact")} className={`${inputCls} w-full`} />
        </label>
        <label>
          <span className={labelCls}>Nature of the injury</span>
          <input aria-label="Nature of the injury" value={form.natureOfInjury} onChange={set("natureOfInjury")} className={`${inputCls} w-full`} />
        </label>
        <label>
          <span className={labelCls}>Part of the body affected</span>
          <input aria-label="Part of the body affected" value={form.bodyPartAffected} onChange={set("bodyPartAffected")} className={`${inputCls} w-full`} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Hospital</span>
          <select aria-label="Hospital" value={form.hospitalAdmission} onChange={set("hospitalAdmission")} className={`${inputCls} w-full`}>
            <option value="">Not recorded</option>
            {HOSPITAL_OPTIONS.map((h) => (
              <option key={h} value={h}>
                {HOSPITAL_LABEL[h]}
              </option>
            ))}
          </select>
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Witnesses</span>
          <input aria-label="Witnesses" value={form.witnesses} onChange={set("witnesses")} placeholder="Names and contact numbers" className={`${inputCls} w-full`} />
        </label>

        {err && (
          <p role="alert" className="sm:col-span-2 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
            {err}
          </p>
        )}
        <div className={`sm:col-span-2 mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Record it"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// "Now", as the value a datetime-local input wants. The office is almost always recording something
// that has just happened, so the field is pre-filled rather than blank.
function localNow(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
